/**
 * Assessment Performance — backport page (single-file legacy variant).
 *
 * This is a deliberately compact, legacy-MUI-v4 reimplementation of
 * the FSD page that lives on refactor/mui-v9-migration under
 * `src/pages/assessment-performance/`. The customer wants the
 * dashboard in production before the full FSD/MUI v9 redesign lands;
 * everything that was a separate widget/feature/entity on the FSD
 * branch is inlined here so the merge conflict (when FSD lands) is
 * limited to the route registration + this file going away.
 *
 * What this file owns:
 *   - Period A / Period B preset (Week-over-Week / Month-over-Month).
 *   - Fetch lifecycle for the metrics endpoint (one redux thunk).
 *   - Header KPIs (4 totals tiles).
 *   - Metric grid (9 metrics, Period A vs Period B + delta %).
 *   - Comparison table (sub-account rows × default 5 metrics).
 *   - Single bar chart (combined-completed totals across periods).
 *
 * What this file deliberately does NOT do (vs. FSD):
 *   - Drill-down modal per account row.
 *   - Email-export dialog (mailto + paste-fallback).
 *   - Custom date-range picker (only WoW / MoM presets).
 *   - Account multi-picker (uses `currentAccount.uuid` only).
 *
 * TEMP: When refactor/mui-v9-migration merges into development, this
 * directory (`src/components/AssessmentPerformance/`) becomes orphan
 * and should be deleted in the same PR that swaps the lazy import in
 * routesByGroups/dashboard.js to `@pages/assessment-performance`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  InputBase,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import SearchIcon from '@material-ui/icons/Search';
import useFetchList from 'hooks/useFetchList';
import {
  endOfMonth,
  endOfWeek,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from 'date-fns';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  fetchAssessmentMetrics,
  PRESET_IDS,
  resetAssessmentPerformance,
  setPeriods,
  setAssessmentSelectedAccounts,
} from 'redux_store';
import { REQUEST_STATUS } from 'redux_store/constants';
// Reuse the low-level Population Health primitive for date ranges —
// slice-agnostic (props in / events out), so it wires straight to the
// assessmentPerformance slice without touching populationHealth.
// The account multi-picker is built inline below (no multi-pick
// primitive on this branch yet).
// TEMP: orphan after FSD merge — the FSD branch has its own
// AccountMultiPicker + custom date-range chip.
import CalendarPopover from 'components/FormikFields/Calendar/CalendarPopover';
import { isoDay, parseIsoDay } from './dateHelpers';
import GetAppIcon from '@material-ui/icons/GetApp';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import {
  buildAccountEmailBody,
  buildAccountEmailSubject,
  buildFullReviewEmailBody,
  buildFullReviewEmailSubject,
} from './emailContent';
import useEmailDraft from './useEmailDraft';
import EmailDraftDialog from './EmailDraftDialog';
import { CUSTOM_TAGS_OPTIONS } from 'components/Shared/CustomTags';

// ============================================================
// Metric dictionary — copied verbatim from
// src/entities/assessment-performance/model/metricDictionary.js on
// refactor/mui-v9-migration. Categories drive delta colour:
//   performance — green when moving the "good" direction (down for
//                 metrics where a rise is bad, see `goodWhenDown`).
//   monitor     — orange on rise (attention), green on fall.
//   neutral     — always grey, no verdict in either direction.
// ============================================================
const METRIC_CATEGORY = { performance: 'performance', monitor: 'monitor', neutral: 'neutral' };
const METRIC_IDS = {
  sent: 'sent',
  combinedCompleted: 'combinedCompleted',
  completed: 'completed',
  eTransfer: 'eTransfer',
  invited: 'invited',
  partialProcessed: 'partialProcessed',
  inProgress: 'inProgress',
  pendingReview: 'pendingReview',
  noShow: 'noShow',
};
const METRIC_DICTIONARY = [
  { id: METRIC_IDS.sent,             labelKey: 'metricSent',             category: METRIC_CATEGORY.neutral,     goodWhenDown: false },
  { id: METRIC_IDS.combinedCompleted, labelKey: 'metricCombinedCompleted', category: METRIC_CATEGORY.performance, goodWhenDown: false },
  { id: METRIC_IDS.completed,        labelKey: 'metricCompleted',        category: METRIC_CATEGORY.performance, goodWhenDown: false },
  { id: METRIC_IDS.eTransfer,        labelKey: 'metricETransfer',        category: METRIC_CATEGORY.performance, goodWhenDown: false },
  { id: METRIC_IDS.invited,          labelKey: 'metricInvited',          category: METRIC_CATEGORY.performance, goodWhenDown: true  },
  { id: METRIC_IDS.partialProcessed, labelKey: 'metricPartialProcessed', category: METRIC_CATEGORY.monitor,     goodWhenDown: true  },
  { id: METRIC_IDS.inProgress,       labelKey: 'metricInProgress',       category: METRIC_CATEGORY.monitor,     goodWhenDown: true  },
  { id: METRIC_IDS.pendingReview,    labelKey: 'metricPendingReview',    category: METRIC_CATEGORY.neutral,     goodWhenDown: false },
  { id: METRIC_IDS.noShow,           labelKey: 'metricNoShow',           category: METRIC_CATEGORY.neutral,     goodWhenDown: false },
];
const METRIC_BY_ID = METRIC_DICTIONARY.reduce((acc, m) => { acc[m.id] = m; return acc; }, {});
const DEFAULT_TABLE_METRIC_IDS = [
  METRIC_IDS.sent,
  METRIC_IDS.combinedCompleted,
  METRIC_IDS.invited,
  METRIC_IDS.inProgress,
  METRIC_IDS.noShow,
];

// ============================================================
// Date helpers — `formatRangeForApi` returns ISO-day strings for the
// backend, which expects `periodAStart=YYYY-MM-DD&periodAEnd=...`.
// `buildPresetRanges` mirrors the FSD `buildPresetRange.js` logic
// 1:1 (Mon–Sun for WoW; previous full calendar month for MoM).
// ============================================================
const ISO_WEEK_OPTIONS = { weekStartsOn: 1 };
const toRange = (startDate, endDate) => ({ start: isoDay(startDate), end: isoDay(endDate) });
const buildPresetRanges = (presetId, today = new Date()) => {
  if (presetId === PRESET_IDS.weekOverWeek) {
    const previousWeekAnchor = subDays(today, 7);
    const periodA = toRange(
      startOfWeek(previousWeekAnchor, ISO_WEEK_OPTIONS),
      endOfWeek(previousWeekAnchor, ISO_WEEK_OPTIONS),
    );
    const weekBeforeAnchor = subDays(startOfWeek(previousWeekAnchor, ISO_WEEK_OPTIONS), 1);
    const periodB = toRange(
      startOfWeek(weekBeforeAnchor, ISO_WEEK_OPTIONS),
      endOfWeek(weekBeforeAnchor, ISO_WEEK_OPTIONS),
    );
    return { periodA, periodB };
  }
  if (presetId === PRESET_IDS.monthOverMonth) {
    const anchor = subMonths(today, 1);
    const periodA = toRange(startOfMonth(anchor), endOfMonth(anchor));
    const monthBeforeAnchor = subMonths(startOfMonth(anchor), 1);
    const periodB = toRange(startOfMonth(monthBeforeAnchor), endOfMonth(monthBeforeAnchor));
    return { periodA, periodB };
  }
  return null;
};
// ============================================================
// Delta computation. Returns null when both values are zero or one
// side is missing — UI renders a neutral em-dash in that case.
// `direction` is +1 / -1 / 0; pair with the metric's `goodWhenDown`
// to pick a colour.
// ============================================================
const computeDelta = (a, b) => {
  const av = Number.isFinite(a) ? a : 0;
  const bv = Number.isFinite(b) ? b : 0;
  if (av === 0 && bv === 0) return null;
  const absolute = av - bv;
  const direction = absolute === 0 ? 0 : absolute > 0 ? 1 : -1;
  const percentage = bv === 0 ? null : (absolute / bv) * 100;
  return { absolute, direction, percentage };
};
const deltaTone = (metric, delta) => {
  if (!delta) return 'neutral';
  if (metric.category === METRIC_CATEGORY.neutral) return 'neutral';
  if (delta.direction === 0) return 'neutral';
  const isGood = metric.goodWhenDown ? delta.direction < 0 : delta.direction > 0;
  if (metric.category === METRIC_CATEGORY.monitor) return isGood ? 'good' : 'attention';
  return isGood ? 'good' : 'bad';
};

// ============================================================
// Styles
// ============================================================
const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  header: { marginBottom: theme.spacing(2) },
  subtitle: { color: theme.palette.text.secondary, marginTop: theme.spacing(0.5) },
  filterBar: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    background: '#f7f9fc',
    border: '1px solid #e3e8ef',
    borderRadius: theme.shape.borderRadius,
  },
  rangeBlock: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 },
  rangeLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  rangeValue: { fontSize: 13, color: theme.palette.text.primary, marginTop: 2 },
  // Shared "field" chrome for every control in the filter bar so
  // preset / date pickers / accounts all read as the same kind of
  // clickable surface — consistent height, border, hover, cursor.
  // CalendarPopover is shared with Population Health and styled
  // internally; we wrap it here to bring it in line with the
  // sibling controls without forking the primitive.
  fieldFrame: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 38,
    background: '#fff',
    border: '1px solid #d3d9e2',
    borderRadius: theme.shape.borderRadius,
    transition: 'border-color 120ms, box-shadow 120ms',
    overflow: 'hidden',
    '&:hover': {
      borderColor: '#a9b3c4',
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}22`,
    },
  },
  // Override CalendarPopover's internal padding/borders so it lays
  // flush inside the fieldFrame wrapper. The popover itself is
  // unchanged; this is purely cosmetic gluing.
  calendarFieldShell: {
    width: '100%',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    '& > div': {
      // Top-level CalendarPopover wrapper has its own border/padding;
      // strip those so the outer fieldFrame is the single border.
      border: 'none !important',
      background: 'transparent !important',
      padding: '0 !important',
      width: '100%',
    },
  },
  // Preset toggle — segmented-control look. White bg matches the
  // input fields; active button gets the primary fill so it still
  // reads as "selected".
  presetGroup: {
    display: 'inline-flex',
    width: '100%',
    height: 38,
    background: '#fff',
    border: '1px solid #d3d9e2',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    '& .MuiButton-root': {
      flex: 1,
      borderRadius: 0,
      border: 'none',
      borderRight: '1px solid #d3d9e2',
      fontSize: 12,
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: 0.2,
      padding: theme.spacing(0, 1),
      minHeight: 36,
      boxShadow: 'none',
    },
    '& .MuiButton-root:last-child': {
      borderRight: 'none',
    },
    '& .MuiButton-contained': {
      boxShadow: 'none',
    },
    '& .MuiButton-outlined': {
      color: theme.palette.text.primary,
      background: 'transparent',
      '&:hover': {
        background: '#f1f4f9',
      },
    },
  },
  kpiTile: {
    padding: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  kpiLabel: { fontSize: 11, fontWeight: 700, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5 },
  kpiValue: { fontSize: 24, fontWeight: 600, color: theme.palette.text.primary },
  kpiDelta: { fontSize: 12, fontWeight: 600 },
  metricCard: {
    padding: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    border: '1px solid #e3e8ef',
    borderRadius: theme.shape.borderRadius,
    background: '#fff',
  },
  metricLabel: { fontSize: 12, fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5 },
  metricValueRow: { display: 'flex', alignItems: 'baseline', gap: theme.spacing(1) },
  metricPrimary: { fontSize: 22, fontWeight: 700 },
  metricSecondary: { fontSize: 12, color: theme.palette.text.secondary },
  deltaPill: {
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 12,
    width: 'fit-content',
  },
  deltaGood:      { background: '#e6f6ec', color: '#1e8e3e' },
  deltaBad:       { background: '#fce8e6', color: '#d93025' },
  deltaAttention: { background: '#fff4e0', color: '#b35900' },
  deltaNeutral:   { background: '#eef0f2', color: '#5f6368' },
  sectionTitle: { fontSize: 16, fontWeight: 700, marginTop: theme.spacing(3), marginBottom: theme.spacing(1) },
  emptyState: {
    padding: theme.spacing(6, 3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  loadingBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6, 3),
    gap: theme.spacing(2),
  },
  errorState: {
    padding: theme.spacing(3),
    background: '#fce8e6',
    color: '#d93025',
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #f8c9c4',
  },
  chartCard: {
    padding: theme.spacing(2),
    border: '1px solid #e3e8ef',
    borderRadius: theme.shape.borderRadius,
    background: '#fff',
    height: 360,
  },
  totalsRow: { fontWeight: 700, background: '#f7f9fc' },
  // Page toolbar — sits between the filter bar and the comparison
  // table. Holds the export + email actions.
  toolbar: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
  },
  // Comparison-table cell content: "previousValue → value (Δ%)" —
  // matches the FSD ComparisonTable / DeltaCell layout.
  deltaCellWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    fontSize: 13,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  deltaCellNumbers: {
    color: theme.palette.text.primary,
    fontVariantNumeric: 'tabular-nums',
  },
  // Comparison table rows are clickable — they open the drilldown modal.
  clickableRow: {
    cursor: 'pointer',
    '&:hover': { background: '#f3f6fb' },
    transition: 'background-color 120ms ease',
  },
  // ----- Drilldown modal -----
  dialogTitleBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: theme.spacing(1),
  },
  dialogTitleText: {
    fontSize: 18,
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  dialogBody: {
    display: 'flex',
    minHeight: 420,
    padding: 0,
  },
  drillLeft: {
    width: 280,
    flexShrink: 0,
    background: '#f7f9fc',
    borderRight: '1px solid #e3e8ef',
    padding: theme.spacing(2.5, 2.5, 3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    overflowY: 'auto',
  },
  drillRight: {
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(2, 2.5, 2.5),
    overflowX: 'auto',
  },
  drillStatTile: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: theme.spacing(1.25, 1.5),
    background: '#fff',
    border: '1px solid #e3e8ef',
    borderRadius: theme.shape.borderRadius,
  },
  drillStatLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  drillStatValue: {
    fontSize: 22,
    fontWeight: 700,
    color: theme.palette.text.primary,
    fontVariantNumeric: 'tabular-nums',
  },
  drillContextBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    padding: theme.spacing(1.25, 1.5),
    background: '#fff',
    border: '1px solid #e3e8ef',
    borderRadius: theme.shape.borderRadius,
  },
  drillContextHeading: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  drillContextRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    fontSize: 13,
    color: theme.palette.text.primary,
  },
  drillContextRowLabel: {
    color: theme.palette.text.secondary,
  },
  drillRightHead: {
    paddingBottom: theme.spacing(1),
    borderBottom: '1px solid #e3e8ef',
    marginBottom: theme.spacing(1),
    fontSize: 14,
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  drillTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  },
  drillTh: {
    textAlign: 'left',
    padding: theme.spacing(1, 1.5),
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    borderBottom: '1px solid #e3e8ef',
    background: '#f7f9fc',
  },
  drillThNum: { textAlign: 'right' },
  drillTd: {
    padding: theme.spacing(1.25, 1.5),
    borderBottom: '1px solid #f0f3f7',
    color: theme.palette.text.primary,
    fontVariantNumeric: 'tabular-nums',
  },
  drillTdNum: { textAlign: 'right' },
  drillFooterHint: {
    flex: 1,
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  // ----- Multi-account picker (inline) -----
  // Visuals come from `fieldFrame` (shared with the date pickers);
  // this rule only handles the trigger's internal layout + cursor.
  pickerTrigger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1.25),
    width: '100%',
    cursor: 'pointer',
    fontSize: 14,
    background: 'transparent',
  },
  pickerTriggerLabel: {
    flex: 1,
    minWidth: 0,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  pickerTriggerPlaceholder: { color: theme.palette.text.secondary },
  pickerPaper: { width: 320, maxWidth: '90vw' },
  pickerSearchBox: {
    padding: theme.spacing(1, 1.5),
    borderBottom: '1px solid #e3e8ef',
  },
  pickerSearchInput: {
    width: '100%',
    fontSize: 13,
  },
  pickerList: {
    maxHeight: 280,
    overflowY: 'auto',
    padding: 0,
  },
  pickerItem: {
    paddingTop: 4,
    paddingBottom: 4,
  },
  pickerEmpty: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  pickerFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1.5),
    borderTop: '1px solid #e3e8ef',
    gap: theme.spacing(1),
  },
  pickerTagsBox: {
    padding: theme.spacing(1, 1.5),
    borderBottom: '1px solid #e3e8ef',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  pickerTagsLabel: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  pickerTagsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
  },
  pickerTag: {
    border: '1px solid #d0d6dd',
    background: '#fff',
    color: theme.palette.text.secondary,
    borderRadius: 999,
    padding: '4px 10px',
    fontSize: 11.5,
    fontWeight: 600,
    letterSpacing: '0.2px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 150ms ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
  },
  pickerTagActive: {
    border: `1px solid ${theme.palette.primary.main}`,
    background: theme.palette.primary.main,
    color: '#fff',
    '&:hover': {
      background: theme.palette.primary.main,
      color: '#fff',
    },
  },
}));

// ============================================================
// AccountMultiPicker — inline multi-select dropdown built on top of
// `useFetchList` against /accounts/{currentAccountUuid}/account-lookups
// (the same endpoint AccountSelector uses for single-pick). A simple
// Popover hosts a search field, scrollable checkbox list, and an
// Apply / Clear footer. Local "draft" selection lets the user toggle
// without firing a fetch on every checkbox click; the final array is
// committed when Apply is pressed.
//
// Why inline (not a separate file): single-file principle for this
// backport. When the FSD branch lands the canonical AccountMultiPicker
// in `@features/account-multi-picker`, this whole block goes away.
// ============================================================
const AccountMultiPicker = ({ classes, paramAccountUuid, currentAccountUuid, value, onChange, placeholder }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [draft, setDraft] = useState(value || []);
  // Tag filter (Account Custom Tags). Local to the popover; resets
  // on close so a stale filter doesn't shrink the list silently on
  // the next visit. Pushed into `useFetchList` alongside the search
  // query as a single combined filter.
  const [selectedTags, setSelectedTags] = useState([]);
  const open = Boolean(anchorEl);

  const baseAccountUuid = paramAccountUuid || currentAccountUuid;
  const endpoint = baseAccountUuid ? `/accounts/${baseAccountUuid}/account-lookups` : null;

  const {
    items: accounts,
    totalCount,
    loading,
    onLoadMoreRows,
    filter,
    onFilter,
    clearFilter,
  } = useFetchList({
    baseEndpoint: endpoint,
    baseParams: {
      fields: ['businessName', 'customIdentifier', 'uuid'],
      sort: 'businessName',
      includeCurrentAccount: false,
    },
    canLoad: open && !!endpoint,
  });

  // Sync draft with committed value when the popover opens — guards
  // against "open / cancel / open again" leaking stale draft state.
  const handleOpen = (e) => {
    setDraft(value || []);
    setSelectedTags([]);
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery('');
    setSelectedTags([]);
  };

  // Search + tag filter dispatch — both pushed through useFetchList
  // as a single combined update so we get debounced refetching for
  // free. Search debounce delegated to onFilter(.., true).
  useEffect(() => {
    if (open) {
      const f = [
        ...(searchQuery ? [{ id: 'businessName', value: searchQuery }] : []),
        ...(selectedTags.length ? [{ id: 'customTags', value: selectedTags }] : []),
      ];
      onFilter(() => f, true);
    } else if (filter && filter.length) {
      clearFilter();
    }
  }, [searchQuery, selectedTags, open, onFilter]);

  const toggleTag = (tagValue) => {
    setSelectedTags((prev) => (
      prev.includes(tagValue)
        ? prev.filter((t) => t !== tagValue)
        : [...prev, tagValue]
    ));
  };

  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 100 && !loading && accounts && accounts.length < totalCount) {
      onLoadMoreRows(accounts.length);
    }
  }, [loading, accounts, totalCount, onLoadMoreRows]);

  const draftHas = (uuid) => draft.some((a) => a.uuid === uuid);
  const toggleDraft = (uuid, businessName) => {
    setDraft((prev) => prev.some((a) => a.uuid === uuid)
      ? prev.filter((a) => a.uuid !== uuid)
      : [...prev, { uuid, businessName }]);
  };

  const handleApply = () => {
    onChange(draft);
    handleClose();
  };
  const handleClear = () => {
    setDraft([]);
    onChange([]);
    handleClose();
  };

  const triggerLabel = (() => {
    if (!value || value.length === 0) return placeholder || 'Select accounts…';
    if (value.length === 1) return value[0].businessName || value[0].uuid;
    return `${value.length} accounts selected`;
  })();

  return (
    <>
      <div className={classes.fieldFrame}>
        <div className={classes.pickerTrigger} onClick={handleOpen} role="button" tabIndex={0}>
          <span className={`${classes.pickerTriggerLabel} ${(!value || !value.length) ? classes.pickerTriggerPlaceholder : ''}`}>
            {triggerLabel}
          </span>
          <KeyboardArrowDownIcon fontSize="small" />
        </div>
      </div>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        classes={{ paper: classes.pickerPaper }}
      >
        <div className={classes.pickerSearchBox}>
          <InputBase
            className={classes.pickerSearchInput}
            placeholder="Search accounts…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            }
          />
        </div>
        <div className={classes.pickerTagsBox}>
          <span className={classes.pickerTagsLabel}>Filter by tags</span>
          <div className={classes.pickerTagsList} role="group" aria-label="Filter by tags">
            {CUSTOM_TAGS_OPTIONS.map((option) => {
              const active = selectedTags.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`${classes.pickerTag} ${active ? classes.pickerTagActive : ''}`}
                  aria-pressed={active}
                  onClick={() => toggleTag(option.value)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className={classes.pickerList} onScroll={handleScroll}>
          {accounts && accounts.length > 0 ? (
            <List dense>
              {accounts.map(({ id: uuid, name: businessName }) => (
                <ListItem
                  button
                  key={uuid}
                  className={classes.pickerItem}
                  onClick={() => toggleDraft(uuid, businessName)}
                >
                  <Checkbox
                    edge="start"
                    color="primary"
                    size="small"
                    checked={draftHas(uuid)}
                    tabIndex={-1}
                    disableRipple
                  />
                  <ListItemText primary={businessName} />
                </ListItem>
              ))}
              {loading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}>
                  <CircularProgress size={18} />
                </div>
              )}
            </List>
          ) : loading ? (
            <div className={classes.pickerEmpty}>
              <CircularProgress size={20} />
            </div>
          ) : (
            <div className={classes.pickerEmpty}>
              {searchQuery ? 'No accounts found' : 'No accounts available'}
            </div>
          )}
        </div>
        <div className={classes.pickerFooter}>
          <Button onClick={handleClear} size="small">Clear</Button>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={handleClose} size="small">Cancel</Button>
            <Button onClick={handleApply} size="small" variant="contained" color="primary">
              Apply
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
};

// ============================================================
// DeltaPill — colour-coded badge under each tile / table cell.
// ============================================================
const DeltaPill = ({ metric, delta, classes, percentOnly = false }) => {
  if (!delta) return <span className={`${classes.deltaPill} ${classes.deltaNeutral}`}>—</span>;
  const tone = deltaTone(metric, delta);
  const cls =
    tone === 'good'      ? classes.deltaGood :
    tone === 'bad'       ? classes.deltaBad :
    tone === 'attention' ? classes.deltaAttention :
                           classes.deltaNeutral;
  const pct = delta.percentage == null
    ? null
    : `${delta.percentage > 0 ? '+' : ''}${delta.percentage.toFixed(0)}%`;
  // `percentOnly` mode is used in the drilldown modal where the
  // absolute delta has its own column — including it inside the
  // pill would duplicate the value next to the same row.
  if (percentOnly) {
    return (
      <span className={`${classes.deltaPill} ${cls}`}>
        {pct || '—'}
      </span>
    );
  }
  const sign = delta.absolute > 0 ? '+' : '';
  return (
    <span className={`${classes.deltaPill} ${cls}`}>
      {sign}{delta.absolute}{pct ? ` (${pct})` : ''}
    </span>
  );
};

// ============================================================
// Page
// ============================================================
const AssessmentPerformancePage = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { currentAccountUuid } = useParams();

  const i18n = useSelector((state) => state.i18n?.en?.assessmentPerformance) || {};
  const titles = useSelector((state) => state.i18n?.en?.titles) || {};

  const currentAccount = useSelector((state) => state.user?.metadata?.currentAccount);
  // Non-DS users have no children to pick from — the multi-picker
  // would render an empty list. Hide it instead and lock the scope
  // to the current account (auto-seeded by the effect below).
  const isDistributionSponsor = useSelector((state) => state.user?.isDistributionSponsor);
  // Defensive fallback — defends against hot-reload race where the
  // store was created before the slice was registered (combineReducers
  // is rebuilt on save but the existing store keeps its old shape
  // until a hard reload). Also future-proofs against the FSD merge
  // remounting the slice under a different namespace.
  const {
    periodA,
    periodB,
    preset,
    selectedAccounts,
    rows,
    totals,
    metricsStatus,
    metricsError,
  } = useSelector((state) => state.assessmentPerformance) || {};

  // Backend requires at least one accountUuid. Empty array means
  // "user has not picked any account yet" — we render an explicit
  // call-to-action prompt and skip the fetch entirely.
  const accountUuidsList = useMemo(
    () => (selectedAccounts || []).map((a) => a.uuid).filter(Boolean),
    [selectedAccounts],
  );
  const hasAccountSelection = accountUuidsList.length > 0;

  // Default columns shown in the comparison table — matches the FSD
  // page (`DEFAULT_TABLE_METRIC_IDS`). Hoisted up here so the CSV
  // export handler below can read it without hitting the temporal
  // dead zone.
  const tableMetricColumns = useMemo(
    () => DEFAULT_TABLE_METRIC_IDS.map((id) => METRIC_BY_ID[id]).filter(Boolean),
    [],
  );

  const [selectedChartMetric, setSelectedChartMetric] = useState(METRIC_IDS.combinedCompleted);

  // Email draft hook — drives both the page-toolbar "Email full
  // review" action and the drilldown modal "Email account summary"
  // action. The dialog is mounted once at page level (below) so it
  // floats above either the page or the drilldown modal as needed.
  const { open: openEmailDraft, dialogProps: emailDialogProps } = useEmailDraft();

  // Drilldown modal — local UI state, not in slice. Keyed off the
  // account uuid; the breakdown rows themselves are read straight
  // out of the metrics rows that already power the comparison table
  // (no second fetch).
  const [drilldownAccountUuid, setDrilldownAccountUuid] = useState(null);
  const drilldownRow = useMemo(() => {
    if (!drilldownAccountUuid || !rows?.length) return null;
    const fromRows = rows.find((r) => (r.uuid || r.name) === drilldownAccountUuid);
    if (fromRows) return fromRows;
    // The synthetic Total row (uuid === 'total') lives in `totals`, not `rows`.
    if (totals && (totals.uuid || totals.name) === drilldownAccountUuid) return totals;
    return null;
  }, [drilldownAccountUuid, rows, totals]);

  // Seed default preset ranges on first mount. Skip if a preset is
  // already set (e.g. user navigated away and came back) — re-applying
  // the preset would refire the network request gratuitously.
  useEffect(() => {
    if (periodA?.start && periodB?.start) return;
    const ranges = buildPresetRanges(preset || PRESET_IDS.weekOverWeek);
    if (ranges) {
      dispatch(setPeriods({
        periodA: ranges.periodA,
        periodB: ranges.periodB,
        preset: preset || PRESET_IDS.weekOverWeek,
      }));
    }
  }, [dispatch, preset, periodA?.start, periodB?.start]);

  // Fire the metrics fetch whenever the periods OR account selection
  // change. Backend requires `accountUuids[]` non-empty, so we gate
  // on `hasAccountSelection` — empty selection renders a CTA prompt
  // instead of an empty table.
  //
  // Two extra guards against an in-flight stale request when the
  // user switches account from the top bar:
  //   - `currentAccount?.uuid === currentAccountUuid` — wait until
  //     the user metadata refresh catches up with the URL. Otherwise
  //     the effect would fire with the new URL but the old
  //     selectedAccounts (still pointing at the previous account),
  //     and the backend would reject the cross-account request.
  //   - `selectedAccounts` membership matches the resolved account
  //     context — guarantees we're not racing the auto-seed effect
  //     above.
  // We pass `accountUuidsList.join(',')` to the dependency array so
  // adding/removing items in the array reliably re-fires the effect
  // (referential equality on the array itself isn't enough here).
  useEffect(() => {
    if (!currentAccountUuid) return;
    if (!hasAccountSelection) return;
    if (currentAccount?.uuid !== currentAccountUuid) return;
    if (!periodA?.start || !periodB?.start) return;
    dispatch(fetchAssessmentMetrics({
      accountUuid: currentAccountUuid,
      periodA,
      periodB,
      accountUuids: accountUuidsList,
    }));
  }, [
    dispatch,
    currentAccountUuid,
    currentAccount?.uuid,
    hasAccountSelection,
    accountUuidsList.join(','),
    periodA?.start,
    periodA?.end,
    periodB?.start,
    periodB?.end,
  ]);

  // Wipe assessmentPerformance state when leaving the page or when
  // the URL account uuid changes mid-mount. Prevents stale rows /
  // totals / selection from a prior account flashing on screen
  // while the new fetch lands.
  useEffect(() => () => {
    dispatch(resetAssessmentPerformance());
  }, [dispatch, currentAccountUuid]);

  // Reset / seed the account selection on every account-context
  // switch (URL account uuid changes, or the DS flag flips):
  //   non-DS: lock the scope to the current account.
  //   DS:     clear so the user picks fresh — guards against a
  //           stale selection from the previous account spilling in
  //           when navigating non-DS → DS or DS-A → DS-B.
  // `selectedAccounts` is intentionally NOT in the dep array — the
  // effect only fires on context change, never on the user's own
  // pick changes within a single DS context.
  useEffect(() => {
    if (!currentAccount?.uuid) return;
    if (isDistributionSponsor) {
      dispatch(setAssessmentSelectedAccounts([]));
    } else {
      dispatch(setAssessmentSelectedAccounts([
        { uuid: currentAccount.uuid, businessName: currentAccount.businessName },
      ]));
    }
  }, [dispatch, isDistributionSponsor, currentAccount?.uuid, currentAccount?.businessName]);

  const handlePreset = useCallback((presetId) => {
    const ranges = buildPresetRanges(presetId);
    if (ranges) {
      dispatch(setPeriods({ periodA: ranges.periodA, periodB: ranges.periodB, preset: presetId }));
    }
  }, [dispatch]);

  // Manual range edits flip the preset chip to "custom" so the user
  // sees that the active preset no longer matches the dates. We
  // accept both [Date, Date] (from CalendarPopover.shouldRange=true)
  // and a falsy value (cleared).
  const handlePeriodChange = useCallback((which) => (value) => {
    const next = { start: null, end: null };
    if (Array.isArray(value) && value[0] && value[1]) {
      next.start = isoDay(value[0]);
      next.end = isoDay(value[1]);
    }
    dispatch(setPeriods({
      [which]: next,
      preset: PRESET_IDS.custom,
    }));
  }, [dispatch]);

  const handleAccountsChange = useCallback((accounts) => {
    dispatch(setAssessmentSelectedAccounts(Array.isArray(accounts) ? accounts : []));
  }, [dispatch]);

  // Full-review email — covers every account currently in the
  // comparison table. Long bodies (~17 accounts) trip the
  // EmailDraftDialog manual-copy fallback inside useEmailDraft.
  const handleFullReviewEmail = useCallback(() => {
    if (!rows?.length) return;
    openEmailDraft({
      subject: buildFullReviewEmailSubject(periodA, periodB),
      body: buildFullReviewEmailBody({ rows, totals, periodA, periodB }),
    });
  }, [rows, totals, periodA, periodB, openEmailDraft]);

  // Single-account email — sourced from the drilldown modal. Always
  // fits in a mailto: URL, so the dialog fallback never fires here in
  // practice; the same hook handles both paths anyway.
  const handleAccountEmail = useCallback((row) => {
    if (!row) return;
    const accountName = row.name || row.uuid || '—';
    openEmailDraft({
      subject: buildAccountEmailSubject(accountName, periodA, periodB),
      body: buildAccountEmailBody(accountName, row.metrics, periodA, periodB),
    });
  }, [periodA, periodB, openEmailDraft]);

  // CSV export — basic comma-separated dump of the comparison table:
  // one header row + one row per account + a totals row. Triggers a
  // browser download via a blob URL. No backend round-trip.
  const handleCsvExport = useCallback(() => {
    if (!rows?.length) return;
    const headers = [
      'Account',
      ...tableMetricColumns.flatMap((m) => {
        const label = i18n[m.labelKey] || m.id;
        return [`${label} (Previous)`, `${label} (Current)`];
      }),
    ];
    const csvRow = (label, source) => [
      label,
      ...tableMetricColumns.flatMap((m) => [cellB(source, m.id), cellA(source, m.id)]),
    ];
    const lines = [
      headers,
      ...rows.map((row) => csvRow(row.name || row.uuid || '—', row)),
    ];
    if (totals) lines.push(csvRow(totals.name || 'Total (all accounts)', totals));
    const csv = lines
      .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `assessment-performance-${periodA?.start || 'periodA'}_${periodB?.start || 'periodB'}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [rows, totals, tableMetricColumns, i18n, periodA, periodB]);

  const isLoading = metricsStatus === REQUEST_STATUS.loading;
  const isFailed = metricsStatus === REQUEST_STATUS.failed;
  const hasData = rows && rows.length > 0;

  // ----- Data shape helpers -----
  // Backend response wraps each row's metric values like
  //   row.metrics[id] = { value, previousValue }
  // where `value` is the recent window (Period A) and
  // `previousValue` is the comparison window (Period B).
  // The two helpers below isolate that mapping so the rest of the
  // component reads from the same source of truth.
  const cellA = (row, id) => row?.metrics?.[id]?.value ?? 0;
  const cellB = (row, id) => row?.metrics?.[id]?.previousValue ?? 0;

  // Bar chart data is the single selected metric across all rows for
  // both periods. Single-account mode means there's usually one bar
  // pair; the chart still works for multi-account mode if/when the
  // backend returns more than one row.
  const chartData = useMemo(() => {
    if (!hasData) return [];
    return rows.map((row) => ({
      name: row.name || row.uuid || '—',
      [i18n.periodALabel || 'Period A']: cellA(row, selectedChartMetric),
      [i18n.periodBLabel || 'Period B']: cellB(row, selectedChartMetric),
    }));
  }, [hasData, rows, selectedChartMetric, i18n.periodALabel, i18n.periodBLabel]);

  return (
    <div className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h5" component="h1">
          {titles.assessmentPerformance || 'Assessment Performance'}
        </Typography>
        <Typography className={classes.subtitle} variant="body2">
          {i18n.pageSubtitle || 'Compare assessment completion across two periods.'}
        </Typography>
      </Box>

      <Box className={classes.filterBar}>
        <Box className={classes.rangeBlock}>
          <span className={classes.rangeLabel}>{i18n.preset || 'Preset'}</span>
          <ButtonGroup
            size="small"
            color="primary"
            className={classes.presetGroup}
            disableElevation
          >
            <Button
              variant={preset === PRESET_IDS.weekOverWeek ? 'contained' : 'outlined'}
              onClick={() => handlePreset(PRESET_IDS.weekOverWeek)}
            >
              {i18n.presetWoW || 'Week over week'}
            </Button>
            <Button
              variant={preset === PRESET_IDS.monthOverMonth ? 'contained' : 'outlined'}
              onClick={() => handlePreset(PRESET_IDS.monthOverMonth)}
            >
              {i18n.presetMoM || 'Month over month'}
            </Button>
          </ButtonGroup>
        </Box>

        <Box className={classes.rangeBlock}>
          <span className={classes.rangeLabel}>{i18n.periodALabel || 'Current'}</span>
          <div className={`${classes.fieldFrame} ${classes.calendarFieldShell}`}>
            <CalendarPopover
              shouldRange
              date={[parseIsoDay(periodA?.start), parseIsoDay(periodA?.end)]}
              onChange={handlePeriodChange('periodA')}
              maxDate={new Date()}
              width="100%"
              placeholder={i18n.periodAPlaceholder || 'Pick current period'}
              updateAfterChanging
            />
          </div>
        </Box>

        <Box className={classes.rangeBlock}>
          <span className={classes.rangeLabel}>{i18n.periodBLabel || 'Previous'}</span>
          <div className={`${classes.fieldFrame} ${classes.calendarFieldShell}`}>
            <CalendarPopover
              shouldRange
              date={[parseIsoDay(periodB?.start), parseIsoDay(periodB?.end)]}
              onChange={handlePeriodChange('periodB')}
              maxDate={new Date()}
              width="100%"
              placeholder={i18n.periodBPlaceholder || 'Pick previous period'}
              updateAfterChanging
            />
          </div>
        </Box>

        {isDistributionSponsor && (
          <Box className={classes.rangeBlock}>
            <span className={classes.rangeLabel}>
              {i18n.accountsLabel || 'Accounts'}
            </span>
            <AccountMultiPicker
              classes={classes}
              paramAccountUuid={currentAccountUuid}
              currentAccountUuid={currentAccount?.uuid}
              value={selectedAccounts || []}
              onChange={handleAccountsChange}
              placeholder={i18n.selectAccountsPlaceholder || 'Select accounts…'}
            />
          </Box>
        )}
      </Box>

      {!hasAccountSelection && (
        <Box className={classes.emptyState}>
          <Typography variant="body2">
            {i18n.pickAccountsPrompt || 'Select at least one account to load metrics.'}
          </Typography>
        </Box>
      )}

      {hasAccountSelection && isLoading && !hasData && (
        <Box className={classes.loadingBox}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="textSecondary">
            {i18n.loading || 'Loading assessment performance…'}
          </Typography>
        </Box>
      )}

      {hasAccountSelection && isFailed && (
        <Box className={classes.errorState}>
          <Typography variant="body2">
            {metricsError || (i18n.errorRetry || 'Could not load metrics. Please retry in a moment.')}
          </Typography>
        </Box>
      )}

      {hasAccountSelection
        && !isLoading
        && !isFailed
        && !hasData
        && metricsStatus === REQUEST_STATUS.succeeded && (
        <Box className={classes.emptyState}>
          <Typography variant="body2">
            {i18n.emptyTitle || 'No data for the selected periods.'}
          </Typography>
        </Box>
      )}

      {hasAccountSelection && hasData && (
        <>
          <Box className={classes.toolbar}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleCsvExport}
              startIcon={<GetAppIcon fontSize="small" />}
              disabled={!rows?.length}
            >
              {i18n.exportCsv || 'Export CSV'}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleFullReviewEmail}
              startIcon={<MailOutlineIcon fontSize="small" />}
              disabled={!rows?.length}
            >
              {i18n.emailFullReview || 'Email full review'}
            </Button>
          </Box>

          {/* Comparison table — primary surface, every row is clickable
              and opens the drilldown modal. The Total row is rendered
              from `totals` (uuid="total" by backend convention) and is
              also clickable. */}
          <Typography className={classes.sectionTitle}>
            {i18n.tableTitle || 'Comparison table'}
          </Typography>
          <Typography variant="body2" className={classes.subtitle} style={{ marginBottom: 8 }}>
            {i18n.tableSubtitle || 'Period A → Period B (Δ%). Click a row to drill down.'}
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small" aria-label="assessment performance comparison">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.tableAccount || 'Account'}</TableCell>
                  {tableMetricColumns.map((metric) => (
                    <TableCell key={metric.id} align="right">
                      {i18n[metric.labelKey] || metric.id}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const rowKey = row.uuid || row.name;
                  return (
                    <TableRow
                      key={rowKey}
                      className={classes.clickableRow}
                      onClick={() => setDrilldownAccountUuid(rowKey)}
                      hover
                    >
                      <TableCell>{row.name || row.uuid || '—'}</TableCell>
                      {tableMetricColumns.map((metric) => {
                        const a = cellA(row, metric.id);
                        const b = cellB(row, metric.id);
                        const delta = computeDelta(a, b);
                        return (
                          <TableCell key={metric.id} align="right">
                            <Box className={classes.deltaCellWrap}>
                              <span className={classes.deltaCellNumbers}>
                                {b.toLocaleString()} → {a.toLocaleString()}
                              </span>
                              <DeltaPill metric={metric} delta={delta} classes={classes} />
                            </Box>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
                {totals && (
                  <TableRow
                    className={`${classes.totalsRow} ${classes.clickableRow}`}
                    onClick={() => setDrilldownAccountUuid(totals.uuid || totals.name)}
                    hover
                  >
                    <TableCell>{totals.name || i18n.tableTotals || 'Totals'}</TableCell>
                    {tableMetricColumns.map((metric) => {
                      const a = cellA(totals, metric.id);
                      const b = cellB(totals, metric.id);
                      const delta = computeDelta(a, b);
                      return (
                        <TableCell key={metric.id} align="right">
                          <Box className={classes.deltaCellWrap}>
                            <span className={classes.deltaCellNumbers}>
                              {b.toLocaleString()} → {a.toLocaleString()}
                            </span>
                            <DeltaPill metric={metric} delta={delta} classes={classes} />
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Visualisations — single bar chart with metric switcher.
              Header KPI tiles and the per-metric grid were intentionally
              dropped in this backport per the customer's "table + chart
              only" feedback; the FSD branch will own the richer KPI
              header when it lands. */}
          <Typography className={classes.sectionTitle}>
            {i18n.chartTitle || 'Visualisations'}
          </Typography>
          <Box style={{ marginBottom: 8 }}>
            <ButtonGroup size="small">
              {METRIC_DICTIONARY.filter((m) => m.category !== METRIC_CATEGORY.neutral).map((m) => (
                <Button
                  key={m.id}
                  variant={selectedChartMetric === m.id ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => setSelectedChartMetric(m.id)}
                >
                  {i18n[m.labelKey] || m.id}
                </Button>
              ))}
            </ButtonGroup>
          </Box>
          <div className={classes.chartCard}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 16, right: 16, bottom: 24, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e3e8ef" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <RechartsTooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey={i18n.periodALabel || 'Period A'} fill="#3f51b5" />
                <Bar dataKey={i18n.periodBLabel || 'Period B'} fill="#9aa6c7" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {/*
        Drilldown modal — opens when a row in the comparison table is
        clicked. Two-pane layout: KPI tiles + active filters on the
        left, full metrics breakdown on the right. Reads its data
        from the already-fetched `rows[i]`, no second network call.
      */}
      <Dialog
        open={Boolean(drilldownRow)}
        onClose={() => setDrilldownAccountUuid(null)}
        maxWidth="md"
        fullWidth
        aria-labelledby="assessment-perf-drilldown-title"
      >
        {drilldownRow && (() => {
          const sentValue = cellA(drilldownRow, METRIC_IDS.sent);
          const combinedValue = cellA(drilldownRow, METRIC_IDS.combinedCompleted);
          const completionRate = sentValue > 0
            ? Math.round((combinedValue / sentValue) * 1000) / 10
            : null;
          return (
            <>
              <DialogTitle id="assessment-perf-drilldown-title" disableTypography>
                <Box className={classes.dialogTitleBar}>
                  <span className={classes.dialogTitleText}>
                    {drilldownRow.name || drilldownRow.uuid || (i18n.drilldownTitle || 'Account breakdown')}
                  </span>
                  <IconButton size="small" onClick={() => setDrilldownAccountUuid(null)} aria-label="close">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent className={classes.dialogBody} dividers>
                <Box className={classes.drillLeft}>
                  <div className={classes.drillStatTile}>
                    <span className={classes.drillStatLabel}>{i18n.kpiSent || 'Sent'}</span>
                    <span className={classes.drillStatValue}>{sentValue.toLocaleString()}</span>
                  </div>
                  <div className={classes.drillStatTile}>
                    <span className={classes.drillStatLabel}>{i18n.kpiCompleted || 'Completed + E-T'}</span>
                    <span className={classes.drillStatValue}>{combinedValue.toLocaleString()}</span>
                  </div>
                  <div className={classes.drillStatTile}>
                    <span className={classes.drillStatLabel}>{i18n.kpiCompletionRate || 'Completion rate'}</span>
                    <span className={classes.drillStatValue}>
                      {completionRate !== null ? `${completionRate}%` : '—'}
                    </span>
                  </div>

                  <div className={classes.drillContextBlock}>
                    <span className={classes.drillContextHeading}>
                      {i18n.drilldownActiveFilters || 'Active filters'}
                    </span>
                    <span className={classes.drillContextRow}>
                      <span className={classes.drillContextRowLabel}>
                        {i18n.periodALabel || 'Period A'}
                      </span>
                      <span>
                        {periodA?.start && periodA?.end
                          ? `${periodA.start} – ${periodA.end}`
                          : '—'}
                      </span>
                    </span>
                    <span className={classes.drillContextRow}>
                      <span className={classes.drillContextRowLabel}>
                        {i18n.periodBLabel || 'Period B'}
                      </span>
                      <span>
                        {periodB?.start && periodB?.end
                          ? `${periodB.start} – ${periodB.end}`
                          : '—'}
                      </span>
                    </span>
                  </div>
                </Box>

                <Box className={classes.drillRight}>
                  <div className={classes.drillRightHead}>
                    {i18n.drilldownMetricsBreakdown || 'Metrics breakdown'}
                  </div>
                  <table className={classes.drillTable}>
                    <thead>
                      <tr>
                        <th className={classes.drillTh}>{i18n.drilldownMetricColumn || 'Metric'}</th>
                        <th className={`${classes.drillTh} ${classes.drillThNum}`}>
                          {i18n.periodALabel || 'Current'}
                        </th>
                        <th className={`${classes.drillTh} ${classes.drillThNum}`}>
                          {i18n.periodBLabel || 'Previous'}
                        </th>
                        <th className={`${classes.drillTh} ${classes.drillThNum}`}>Δ</th>
                        <th className={`${classes.drillTh} ${classes.drillThNum}`}>Δ%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {METRIC_DICTIONARY.map((metric) => {
                        const a = cellA(drilldownRow, metric.id);
                        const b = cellB(drilldownRow, metric.id);
                        const delta = computeDelta(a, b);
                        return (
                          <tr key={metric.id}>
                            <td className={classes.drillTd}>
                              {i18n[metric.labelKey] || metric.id}
                            </td>
                            <td className={`${classes.drillTd} ${classes.drillTdNum}`}>
                              {a.toLocaleString()}
                            </td>
                            <td className={`${classes.drillTd} ${classes.drillTdNum}`}>
                              {b.toLocaleString()}
                            </td>
                            <td className={`${classes.drillTd} ${classes.drillTdNum}`}>
                              {delta
                                ? `${delta.absolute > 0 ? '+' : ''}${delta.absolute}`
                                : '0'}
                            </td>
                            <td className={`${classes.drillTd} ${classes.drillTdNum}`}>
                              <DeltaPill
                                metric={metric}
                                delta={delta}
                                classes={classes}
                                percentOnly
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDrilldownAccountUuid(null)}>
                  {i18n.close || 'Close'}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<MailOutlineIcon fontSize="small" />}
                  onClick={() => handleAccountEmail(drilldownRow)}
                  disabled={!drilldownRow}
                >
                  {i18n.drilldownEmailSummary || 'Email account summary'}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* Long-body fallback for "Email full review" — single-account
          drafts always fit a mailto: URL and never reach this dialog,
          but the same mount serves both flows so a future scope
          change wouldn't need re-wiring. */}
      <EmailDraftDialog {...emailDialogProps} />
    </div>
  );
};

export default AssessmentPerformancePage;
